import {
  Attribute,
  BaseLayerSchemas,
  BaseStyleId,
  Bucket,
  DateFilter,
  defaultLayerAttribute,
  isAutoAttribute,
  layerAttributes,
  MapLayer,
} from "@cartographerio/atlas-map";
import { Feature } from "@cartographerio/geometry";
import { AccessToken, WorkspaceRef } from "@cartographerio/types";
import lodash from "lodash";
import mapboxgl, { MapboxGeoJSONFeature, MapSourceDataEvent } from "mapbox-gl";
import hash from "object-hash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { createMapboxLayers, createMapboxSources } from "../../../compile";
import { attributeBuckets } from "../../../compile/bucket";
import { mapboxSourceIds } from "../../../compile/source";
import { MapboxAnyLayer, MapboxLayer, MapboxSources } from "../../../mapbox";
import diffDicts from "../../../utils/diffDicts";
import useDebouncedQueue from "../useDebouncedQueue";
import useDict, { Dict } from "../useDict";
import usePrevious from "../usePrevious";
import useClickSelectionHandler from "./useClickSelectionHandler";
import useDragSelectionHandler from "./useDragSelectionHandler";

export interface UseCartographerMapProps {
  map: mapboxgl.Map | null;
  layers: MapLayer[];
  baseStyles: BaseLayerSchemas;
  apiUrl?: string;
  accessToken?: AccessToken;
  defaultWorkspace?: WorkspaceRef;
  baseStyleId: BaseStyleId;
}

export interface UseCartographerMapResult {
  layerVisibility: Dict<boolean>;
  setLayerVisibility: (
    layerId: string,
    visibility: boolean | undefined
  ) => void;
  selectedLayer: MapLayer | undefined;
  setSelectedLayer: (layer: MapLayer | undefined) => void;
  currentAttributes: Record<string, Attribute>;
  currentBuckets: Record<string, Record<string, Bucket[]>>;
  setCurrentAttribute: (layer: MapLayer, attr: Attribute) => void;
  selectedFeatures: Record<string, Feature[]>;
  setSelectedFeatures: (features: Record<string, Feature[]>) => void;
  dateFilter: DateFilter;
  setDateFilter: (filter: DateFilter) => void;
}

const mapSourceDataEventSourceId = (evt: MapSourceDataEvent) => evt.sourceId;

function createBuckets(
  map: mapboxgl.Map | null,
  layer: MapLayer
): Dict<Bucket[]> {
  let features: Feature[];

  const attributes = layerAttributes(layer);

  try {
    // Only query the map if we absolutely have to:
    if (attributes.some(isAutoAttribute)) {
      features = lodash
        .chain(mapboxSourceIds(layer))
        .flatMap(sourceId => map?.querySourceFeatures(sourceId) ?? [])
        .uniq()
        .value();
    } else {
      features = [];
    }
  } catch (error) {
    // source may not exist
    features = [];
  }

  return lodash.fromPairs(
    attributes.map(attr => [attr.attributeId, attributeBuckets(attr, features)])
  );
}

export default function useCartographerMap(
  props: UseCartographerMapProps
): UseCartographerMapResult {
  const {
    map,
    apiUrl,
    accessToken,
    defaultWorkspace,
    baseStyles,
    baseStyleId,
    layers,
  } = props;

  const [layerVisibility, setLayerVisibility] = useDict<boolean>(
    lodash.fromPairs(
      layers.map(layer => [layer.layerId, layer.defaultVisibility ?? true])
    )
  );

  const [selectedLayer, setSelectedLayer] = useState<MapLayer | undefined>(
    layers.length === 1 ? layers[0] : undefined
  );

  const [selectedFeatures, setSelectedFeatures] = useState<Dict<Feature[]>>({});

  const [dateFilter, setDateFilter] = useState<DateFilter>({
    from: null,
    to: null,
  });

  const [currentAttributes, internalSetCurrentAttribute] = useDict(
    lodash.fromPairs(
      layers.map(layer => [layer.layerId, defaultLayerAttribute(layer)])
    )
  );

  // Dictionary of layer ID to...
  //   Array of attribute index to...
  //     Array of buckets
  const initialBuckets: Dict<Dict<Bucket[]>> = useMemo(
    () =>
      lodash.fromPairs(
        layers.map(layer => [
          layer.layerId,
          lodash.fromPairs(
            layerAttributes(layer).map(attr => [
              attr.attributeId,
              attributeBuckets(attr, []),
            ])
          ),
        ])
      ),
    [layers]
  );

  const [currentBuckets, internalSetCurrentBuckets] = useDict(initialBuckets);

  const setCurrentAttribute = useCallback(
    (layer: MapLayer, attribute: Attribute) => {
      internalSetCurrentAttribute(layer.layerId, attribute);
    },
    [internalSetCurrentAttribute]
  );

  const onSourcesLoaded = useCallback(
    (sourceIds: string[]) => {
      layers.forEach(layer => {
        if (mapboxSourceIds(layer).some(id => sourceIds.includes(id))) {
          internalSetCurrentBuckets(layer.layerId, createBuckets(map, layer));
        }
      });
    },
    [map, layers, internalSetCurrentBuckets]
  );

  const onSourceDataLoad = useDebouncedQueue(
    mapSourceDataEventSourceId,
    onSourcesLoaded,
    500
  );

  useEffect(() => {
    if (map == null) return;
    map.on("sourcedata", onSourceDataLoad);

    return () => {
      map.off("sourcedata", onSourceDataLoad);
    };
  }, [map, onSourceDataLoad]);

  const [mapboxSources, setMapboxSources] = useDict<MapboxSources>();
  const [mapboxLayers, setMapboxLayers] = useDict<MapboxLayer[]>();

  // const prevLayers = usePrevious(layers, []);
  const prevMapboxSources = usePrevious(map == null ? {} : mapboxSources);
  const prevMapboxLayers = usePrevious(map == null ? {} : mapboxLayers);

  // CREATE MAPBOX SOURCES AND LAYERS

  const updateLayer = useCallback(
    (layer: MapLayer) => {
      const attribute =
        currentAttributes[layer.layerId] ?? defaultLayerAttribute(layer);

      const buckets =
        currentBuckets[layer.layerId] ?? attributeBuckets(attribute);

      const s = createMapboxSources(layer, {
        apiUrl,
        accessToken,
        defaultWorkspace,
      });
      const l = createMapboxLayers(
        layer,
        attribute,
        buckets[attribute.attributeId] ?? [],
        dateFilter,
        selectedFeatures[layer.layerId] ?? []
      );

      setMapboxSources(layer.layerId, s);
      setMapboxLayers(layer.layerId, l);
    },
    [
      apiUrl,
      accessToken,
      defaultWorkspace,
      currentAttributes,
      currentBuckets,
      setMapboxLayers,
      setMapboxSources,
      dateFilter,
      selectedFeatures,
    ]
  );

  useEffect(() => {
    layers.forEach(updateLayer);
  }, [layers, updateLayer]);

  // REGISTER MAPBOX SOURCES AND LAYERS WITH MAPBOX GL

  useEffect(() => {
    if (map == null) return;

    const combinedSources: MapboxSources = Object.values(mapboxSources).reduce(
      (a, b) => Object.assign(a, b),
      {}
    );

    const prevCombinedSources: MapboxSources = Object.values(
      prevMapboxSources
    ).reduce((a, b) => Object.assign(a, b), {});

    const sourcesAdded = lodash.omit(
      combinedSources,
      Object.keys(prevCombinedSources)
    );

    const sourcesRemoved = lodash.omit(
      prevCombinedSources,
      Object.keys(combinedSources)
    );

    const {
      added: layersAdded,
      removed: layersRemoved,
      updated: layersUpdated,
    } = diffDicts(
      mapboxLayers,
      prevMapboxLayers,
      (a, b) => hash.MD5(a) === hash.MD5(b)
    );

    [...layersUpdated, ...layersRemoved].forEach(update => {
      update.oldValue.forEach(layer => {
        console.debug("Removing mapbox layer", layer.id);
        map.removeLayer(layer.id);
      });
    });

    Object.entries(sourcesRemoved).forEach(([id, _source]) => {
      console.debug("Removing mapbox source", id);
      map.removeSource(id);
    });

    Object.entries(sourcesAdded).forEach(([id, source]) => {
      console.debug("Registering mapbox source", id);
      map.addSource(id, source);
    });

    // TODO: Preserve layer order specified in map schema!
    // This call to reverse() is a terrible hack that works 20% of the time.
    [...layersAdded, ...layersUpdated].reverse().forEach(update => {
      update.newValue.forEach(layer => {
        console.debug("Registering mapbox layer", layer.id);
        map.addLayer(
          layer as MapboxAnyLayer,
          baseStyles[baseStyleId].insertBefore
        );
      });
    });
  }, [
    map,
    baseStyles,
    baseStyleId,
    mapboxSources,
    prevMapboxSources,
    mapboxLayers,
    prevMapboxLayers,
  ]);

  // LAYER VISIBILITY

  useEffect(() => {
    if (map == null) return;

    lodash.each(mapboxLayers, (mbLayers, layerId) => {
      const visible = layerVisibility[layerId] || false;

      mbLayers.forEach(mbLayer => {
        map.setLayoutProperty(
          mbLayer.id,
          "visibility",
          visible ? "visible" : "none"
        );
      });
    });
  }, [map, mapboxLayers, layerVisibility]);

  // SELECTION HANDLERS

  const setUniqueSelectedFeatures = useCallback(
    (featuresByMapboxSource: Dict<MapboxGeoJSONFeature[]>) => {
      // `featuresByMapboxSource` is grouped by Mapbox source.

      const featuresByLayer: Dict<MapboxGeoJSONFeature[]> = lodash
        .chain(layers)
        .map(layer => [
          layer.layerId,
          mapboxSourceIds(layer)
            .map(sourceId => featuresByMapboxSource[sourceId] ?? [])
            .reduce((a, b) => [...a, ...b], []),
        ])
        .fromPairs()
        .value();

      // if (lodash.isEmpty(selectedFeatures)) return;

      setSelectedFeatures(featuresByLayer);

      // If we've only selected features on one layer, select that layer:

      const layersSelected = Object.keys(featuresByLayer);

      if (layersSelected.length === 1) {
        const layerToSelect = layersSelected[0];
        layers.forEach(layer => {
          if (layer.layerId === layerToSelect) {
            setSelectedLayer(layer);
            return;
          }
        });
      }
    },
    [layers, setSelectedFeatures]
  );

  // CLICK SELECTION HANDLER

  useClickSelectionHandler({
    map,
    mapboxSources,
    setUniqueSelectedFeatures,
  });

  // SHIFT-CLICK+DRAG SELECTION HANDLER

  useDragSelectionHandler({
    map,
    mapboxSources,
    setUniqueSelectedFeatures,
  });

  return {
    layerVisibility,
    setLayerVisibility,
    selectedLayer,
    setSelectedLayer,
    currentAttributes,
    currentBuckets,
    setCurrentAttribute,
    selectedFeatures,
    setSelectedFeatures,
    dateFilter,
    setDateFilter,
  };
}
